import { tabKeys, useTabFocus } from './accessibility.jsx';
import { Icon } from './icons.jsx';
import { ScopeSettings } from './settings-scope.jsx';
import { McpOAuthClients } from './settings-mcp.jsx';
import { INSTALLATION, scopesFor } from './settings-scope-logic.mjs';
/**
 * Admin settings: the five-tab shell, the registered companies, and the
 * shared once-only copy box. Everything else lives in a per-tab file --
 * the Agent tab in settings-agent.jsx, where the LLM key now sits as a
 * property of the selected provider rather than a card of its own.
 *
 * Every secret this page mints is shown ONCE, in a copy-box, with the
 * warning inline rather than in a dismissable toast -- a toast can be
 * gone before it is read, and the value is not recoverable.
 *
 * Exposed as window.SettingsPage.
 */
const { useState, useEffect, useRef } = React;

function OnceBox({ label, value, lang, note }) {
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const valRef = useRef(null);
  return (
    <div className="set-once">
      <div className="set-once-warn">
        {lang === 'he'
          ? 'זו הפעם היחידה שהערך הזה יוצג. העתק אותו עכשיו — הוא אינו ניתן לשחזור.'
          : 'This is the only time this value will be shown. Copy it now — it is not recoverable.'}
      </div>
      {note && <div className="set-once-note">{note}</div>}
      <div className="set-once-label" dir="auto">{label}</div>
      <div className="set-once-row">
        <code className="set-once-val" dir="ltr" ref={valRef}>{value}</code>
        {/* The deployment is plain http on a bare IP, which is NOT a secure
            context, so navigator.clipboard is undefined there and an
            unguarded call throws inside onClick with nothing on screen.
            Fall back to selecting the value so Ctrl+C still works. The
            fallback uses a per-instance ref rather than a shared id lookup:
            the Access tab mounts window.UsersSection and CompaniesSection
            together, and each can have a mounted OnceBox at once -- looking
            the id up in the document would always resolve to the first
            one, letting a click on the second box select and "copy" the
            first box's secret instead. */}
        <button type="button" className="btn btn-sm" onClick={() => {
          const selectIt = () => {
            const el = valRef.current;
            if (el && window.getSelection) {
              const r = document.createRange(); r.selectNodeContents(el);
              const sel = window.getSelection();
              sel.removeAllRanges(); sel.addRange(r);
            }
            setCopied(false);
            setManual(true);
          };
          try {
            if (window.isSecureContext && navigator.clipboard) {
              navigator.clipboard.writeText(value).then(() => { setCopied(true); setManual(false); }).catch(selectIt);
            } else { selectIt(); }
          } catch (_) { selectIt(); }
        }}>{copied ? (lang === 'he' ? 'הועתק' : 'copied') : (lang === 'he' ? 'העתק' : 'copy')}</button>
      </div>
      <p role="status" aria-live="polite">{copied ? (lang === 'he' ? 'הועתק ללוח.' : 'Copied to clipboard.') : manual
        ? (lang === 'he' ? 'ההעתקה האוטומטית אינה זמינה. הערך נבחר; השתמשו ב-Ctrl+C או Cmd+C.' : 'Automatic copy is unavailable. The value is selected; use Ctrl+C or Cmd+C.') : ''}</p>
    </div>
  );
}

function CompaniesSection({ lang }) {
  const [tenants, setTenants] = useState(null);
  const [minted, setMinted] = useState(null);
  const [error, setError] = useState('');

  const load = () => PCMAdapter.getTenants()
    .then((d) => setTenants(d.tenants))
    .catch((e) => { setTenants([]); setError(String(e.message || e)); });

  useEffect(() => { load(); }, []);

  const rotate = (t) => {
    setError(''); setMinted(null);
    PCMAdapter.rotateTenantSecret(t.id)
      .then((d) => { setMinted(d); load(); })
      .catch((e) => setError(String(e.message || e)));
  };

  if (tenants === null) return <div className="card set-section wide">{lang === 'he' ? 'טוען…' : 'Loading…'}</div>;

  return (
    <div className="card set-section wide">
      <h3>{lang === 'he' ? 'חברות רשומות' : 'Registered companies'}</h3>
      <p className="set-note" dir="auto">
        {lang === 'he'
          ? 'כל חברה שנרשמה מול שרת האיסוף הזה: מפתח ה-API שלה (מזהה, לא סוד), אזור הזמן שלפיו מתפרשים תאריכי Priority שלה, ומתי שלחה נתונים לאחרונה.'
          : "Every company registered against this collector: its API key (an identifier, not a credential), the timezone its Priority dates are read in, and when it last sent data."}
      </p>
      <p className="set-note" dir="auto">
        {lang === 'he'
          ? 'הסוד עצמו אינו ניתן להצגה — נשמר כ-hash חד-כיווני. סבב מייצר ערך חדש ומציג אותו פעם אחת, והלקוח ב-Priority צריך את הערך החדש כדי להמשיך לשלוח.'
          : 'The secret itself cannot be shown — only a one-way hash is stored. Rotating mints a new one and shows it once, and the Priority-side client needs that new value to keep sending.'}
      </p>
      <div className="tbl-wrap" tabIndex={0}>
        <table className="tbl">
          <thead>
            <tr>
              <th>{lang === 'he' ? 'חברה' : 'Company'}</th>
              <th>api_key</th>
              <th>slug</th>
              <th>{lang === 'he' ? 'אזור זמן' : 'Timezone'}</th>
              <th>{lang === 'he' ? 'קליטה אחרונה' : 'Last ingest'}</th>
              <th>{lang === 'he' ? 'פעיל' : 'Active'}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id}>
                <td dir="auto">{t.company_name}</td>
                <td className="mono" dir="ltr">{t.api_key}</td>
                <td className="mono" dir="ltr">{t.slug || '—'}</td>
                <td className="mono" dir="ltr">{t.timezone}</td>
                <td className="mono" dir="ltr">
                  {t.last_ingest_at ? `${t.last_ingest_at.replace('T', ' ').slice(0, 19)} UTC` : '—'}
                </td>
                <td className="mono">{t.is_active ? 'Y' : 'N'}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => rotate(t)}>
                    {lang === 'he' ? 'סובב סוד' : 'Rotate secret'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
      {minted && (
        <OnceBox label={`${minted.company_name} · api_secret`}
                 value={minted.api_secret} lang={lang}/>
      )}
    </div>
  );
}

const TABS = [
  { id: 'agent',   en: 'Agent',   he: 'סוכן' },
  { id: 'access',  en: 'Access',  he: 'גישה' },
  { id: 'voice',   en: 'Voice',   he: 'קול' },
  { id: 'secrets', en: 'Secrets', he: 'סודות' },
  { id: 'system',  en: 'System',  he: 'מערכת' },
  { id: 'companies', en: 'Companies', he: 'חברות' },
  { id: 'proposals', en: 'Skill proposals', he: 'הצעות לכישורים' },
  { id: 'defaults', en: 'Company defaults', he: 'ברירות מחדל לחברות' },
];
// A company's own settings (spec 2026-10-18-full-separation §7.2): the one tab of a
// company scope, and the page of a company admin, which is locked to its own company.
const COMPANY_TAB = { id: 'company', en: 'Company settings', he: 'הגדרות החברה' };
const TENANT_TABS = [COMPANY_TAB, { id: 'companies', en: 'Companies', he: 'חברות' }];
const TAB_ICONS = {
  agent: <><rect x="5" y="5" width="14" height="14" rx="3"/><path d="M9 1v4m6-4v4M9 19v4m6-4v4M1 9h4m-4 6h4m14-6h4m-4 6h4"/></>,
  access: <><circle cx="9" cy="8" r="3"/><path d="M3 21v-3a6 6 0 0 1 12 0v3m2-16a3 3 0 0 1 0 6m2 3a5 5 0 0 1 2 4v3"/></>,
  voice: <><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0m-7 7v5m-4 0h8"/></>,
  secrets: <><circle cx="8" cy="15" r="5"/><path d="m12 11 9-9m-4 4 3 3m-6 0 3 3"/></>,
  system: <><path d="M4 6h16M4 12h16M4 18h16"/><circle cx="8" cy="6" r="2"/><circle cx="16" cy="12" r="2"/><circle cx="10" cy="18" r="2"/></>,
  companies: <><path d="M4 22V3h12v19m0-12h4v12M1 22h22M8 7h4m-4 4h4m-4 4h4m-4 7v-3h4v3"/></>,
  proposals: <><path d="M12 5c-3-2-7-2-10-1v16c3-1 7-1 10 1m0-16c3-2 7-2 10-1v16c-3-1-7-1-10 1V5"/></>,
  defaults: <><path d="M3 21h18M5 21V9l7-5 7 5v12"/><path d="M9 21v-6h6v6"/></>,
  company: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h8M8 11h8M8 15h5"/></>,
};

function SettingsPage({ lang, me }) {
  const he = lang === 'he';
  const admin = PCMAdapter.getRole() === 'admin';
  const accountKey = JSON.stringify([PCMAdapter.getRole(), me?.user_id, me?.tenant_id,
    me?.tenant_admin, me?.via_admin, admin ? PCMAdapter.getAdminToken() : PCMAdapter.getToken()]);
  // A company admin's page is locked to its own tenant (§7.2): no scope switch.
  const own = admin ? null : (scopesFor('tenant', me, [])[0] || null);
  const [scope, setScope] = useState(INSTALLATION);
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    if (!admin) return;
    PCMAdapter.getTenants().then((d) => setTenants(d.tenants || [])).catch(() => setTenants([]));
  }, [admin]);
  // The shell routes these hashes to Settings; this page owns the active tab. A hash the
  // current scope has no tab for shows that scope's first tab.
  const hashTab = () => (window.location.hash || '').replace(/^#/, '');
  const [tab, setTab] = useState(hashTab);
  const tabs = own ? TENANT_TABS : scope === INSTALLATION ? TABS : [COMPANY_TAB];
  const active = tabs.some((x) => x.id === tab) ? tab : tabs[0].id;
  const tabFocus = useTabFocus(`settings-tab-${active}`);
  useEffect(() => {
    const onHash = () => setTab(hashTab());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = (id) => { window.location.hash = id; setTab(id); };

  if (!admin && !own) {
    return (
      <div className="card set-section wide">
        <p className="set-note" dir="auto">{he ? 'ההגדרות שמורות למנהלי החברה.' : "Settings are for your company's admin."}</p>
      </div>
    );
  }

  return (
    <>
      <div className="settings-header">
        <div>
          <h1 className="page-title">{he ? 'הגדרות' : 'Settings'}</h1>
          <p className="settings-intro">{own
            ? (he ? 'ההגדרות של החברה שלך.' : "Your company's settings.")
            : (he ? 'ניהול הסוכן, הצוות והחברות המחוברות.' : 'Manage your agent, team, and connected companies.')}</p>
        </div>
        <details className="settings-help">
          <summary>{he ? 'איך ההגדרות פועלות' : 'How settings work'}</summary>
          <p className="set-note" dir="auto">
        {he
          ? 'ההגדרות נשמרות במסד הנתונים — בלי הפעלה מחדש ובלי לערוך .env על השרת. בחירת SDK חלה על שיחות וריצות חדשות; שינוי מודל חל בתור השיחה או במקטע ההרצה הבא, אלא אם נקבע מודל מפורש. סוד המנהל כפוף ל-SETTINGS_ENV_WINS; ראה את ההערה בסעיף עצמו.'
          : 'Settings are stored in the database — no restart and no editing .env on the server. SDK selection applies to new conversations and runs; model changes apply to the next chat turn or Workbench leg unless a model is explicitly set. The admin secret follows SETTINGS_ENV_WINS; its section says which mode is in force.'}
          </p>
          <p className="set-note" dir="auto">{he
            ? 'כל חברה יורשת את ברירות המחדל של ההתקנה, עד שנקבע לה ערך משלה.'
            : 'Every company inherits the installation defaults until it is given a value of its own.'}</p>
        </details>
      </div>
      {!own && (
        <div className="set-row">
          <label className="set-field-label" htmlFor="settings-scope">{he ? 'היקף' : 'Scope'}</label>
          <select id="settings-scope" className="set-input" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value={INSTALLATION}>{he ? 'התקנה' : 'Installation'}</option>
            {tenants.map((x) => <option key={x.id} value={String(x.id)}>{x.company_name}</option>)}
          </select>
        </div>
      )}
        <div ref={tabFocus.ref} className="tabs settings-tabs" role="tablist" aria-label={he ? 'הגדרות' : 'Settings'} onKeyDown={tabKeys} onFocus={tabFocus.onFocus}>
        {tabs.map((x) => (
          <button key={x.id} type="button" role="tab"
                  id={`settings-tab-${x.id}`} aria-controls="settings-panel" tabIndex={tabFocus.focused === `settings-tab-${x.id}` ? 0 : -1}
                  aria-selected={active === x.id}
                  className={`tab${active === x.id ? ' active' : ''}`}
                  onClick={() => go(x.id)}><Icon d={TAB_ICONS[x.id]}/>{he ? x.he : x.en}</button>
        ))}
      </div>
      <div className="set-page settings-page" id="settings-panel" role="tabpanel" aria-labelledby={`settings-tab-${active}`} tabIndex={0}>
        {/* Only the active pane mounts. That is what lets the agent pane
            grow without pushing seven other cards off the screen, and it
            is also why each moved component keeps its own fetch-on-mount:
            switching tabs re-fetches. Accepted -- the alternative is
            hoisting eight components' state into this shell. */}
        {own ? <>
          {active === 'company' && <React.Fragment key={`${accountKey}:${own}`}>
            <ScopeSettings lang={lang} scope={own}/><McpOAuthClients lang={lang} scope={own}/><window.ChannelCards lang={lang} scope={own}/>
          </React.Fragment>}
          {active === 'companies' && <window.CompaniesOnboarding key={own} lang={lang} tenantId={Number(own)} readOnly/>}
        </> : scope !== INSTALLATION ? (
          <React.Fragment key={`${accountKey}:${scope}`}>
            <ScopeSettings lang={lang} scope={scope}/><McpOAuthClients lang={lang} scope={scope}/><window.ChannelCards lang={lang} scope={scope}/>
          </React.Fragment>
        ) : <>
          {active === 'agent'   && <window.AgentSettings lang={lang}/>}
          {active === 'access'  && <>
            <window.UsersSection lang={lang}/>
            <window.PermissionsSection lang={lang}/>
            <CompaniesSection lang={lang}/>
          </>}
          {active === 'voice'   && <window.VoiceSettings lang={lang}/>}
          {active === 'secrets' && <window.SecretsSettings lang={lang}/>}
          {active === 'system'  && <window.SystemSettings lang={lang}/>}
          {active === 'companies' && <window.CompaniesOnboarding lang={lang}/>}
          {active === 'proposals' && <window.SkillProposalsPage lang={lang}/>}
          {active === 'defaults' && <ScopeSettings lang={lang} scope={INSTALLATION} defaultsOnly/>}
        </>}
      </div>
    </>
  );
}

Object.assign(window, { SettingsPage, OnceBox });
